import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';

const ago = (v) => {
  if (!v) return '';
  const s = Math.floor((Date.now() - new Date(v).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const stamp = (v) =>
  v ? new Date(v).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '';

const WaIcon = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export default function WhatsappMenu({ base }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ contacts: [], total_numbers: 0, total_messages: 0, last_24h: 0 });
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  // Agent: claim the candidate, then open their edit form.
  // Admin: read-only, just show the first message.
  const claim = async (c) => {
    if (!base.startsWith('/agent')) {
      setSelected(c);
      return;
    }
    try {
      const r = await api.post(`${base}/claim`, { user_id: c.user_id });
      setOpen(false);
      loadContacts();
      if (r.data.type === 'employer') {
        navigate('/agent/employers/' + r.data.employer.id, { state: { openEdit: true } });
      } else {
        navigate('/agent/candidates', { state: { editCandidate: r.data.candidate } });
      }
    } catch (e) {
      alert((e.response && e.response.data && e.response.data.message) || 'Could not add candidate');
    }
  };

  const loadContacts = useCallback(() => {
    return api
      .get(`${base}/contacts`)
      .then((r) => setData(r.data))
      .catch((e) => console.error('WhatsApp contacts failed', e));
  }, [base]);

  // badge count: load on mount and refresh every minute
  useEffect(() => {
    loadContacts();
    const t = setInterval(loadContacts, 60000);
    return () => clearInterval(t);
  }, [loadContacts]);

  // refresh list whenever the dropdown opens
  useEffect(() => {
    if (!open || selected) return;
    setLoading(true);
    loadContacts().finally(() => setLoading(false));
  }, [open, selected, loadContacts]);

  // load one number's chat
  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api
      .get(base, { params: { phone: selected.phone } })
      .then((r) => setMessages((r.data.data || []).slice().reverse()))
      .catch((e) => console.error('WhatsApp messages failed', e))
      .finally(() => setLoading(false));
  }, [selected, base]);

  // close on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const term = search.trim().toLowerCase();
  const shown = (data.contacts || []).filter(
    (c) =>
      !term ||
      String(c.phone).includes(term) ||
      (c.contact_name || '').toLowerCase().includes(term)
  );
  const badge = data.last_24h > 99 ? '99+' : data.last_24h;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="WhatsApp messages"
        className="relative w-10 h-10 rounded-xl bg-[#25D366] text-white flex items-center justify-center shadow-sm hover:brightness-95"
      >
        <WaIcon className="w-6 h-6" />
        {data.last_24h > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center border-2 border-white">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-3 w-[92vw] sm:w-[360px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-30">
          {!selected ? (
            <>
              <div className="bg-gradient-to-r from-[#25D366] to-[#1FBF5B] text-white px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="font-bold text-base leading-tight flex items-center gap-2">
                    <WaIcon className="w-5 h-5" /> WhatsApp Chats
                  </div>
                  <div className="text-xs text-white/90 text-right leading-tight">
                    {data.total_numbers} numbers<br />{data.last_24h} in last 24h
                  </div>
                </div>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name or number"
                  className="mt-3 w-full rounded-lg px-3 py-1.5 text-sm text-gray-800 placeholder-gray-400 outline-none"
                />
              </div>

              <div className="max-h-[360px] overflow-y-auto">
                {loading && <div className="p-6 text-center text-sm text-gray-400">Loading…</div>}
                {!loading && shown.length === 0 && (
                  <div className="p-6 text-center text-sm text-gray-400">No messages</div>
                )}
                {!loading &&
                  shown.map((c) => {
                    const fresh = c.last_at && Date.now() - new Date(c.last_at).getTime() < 3600000;
                    return (
                      <button
                        key={c.phone}
                        onClick={() => claim(c)}
                        className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-green-50 ${
                          fresh ? 'bg-green-50/70' : 'bg-white'
                        }`}
                      >
                        <div className="flex justify-between gap-2">
                          <span className="font-semibold text-sm text-[#0D1B3D] truncate">
                            {c.contact_name || c.phone}
                          </span>
                          <span className="text-[11px] text-gray-400 shrink-0">{ago(c.last_at)}</span>
                        </div>
                        <div className="text-xs text-gray-700 mt-1">📞 {c.phone}</div>
                        <div className="text-xs text-gray-500 italic mt-1 truncate">
                          💬 {c.last_body || `[${c.last_media || 'media message'}]`}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-[10px] font-bold text-green-700 bg-green-100 rounded px-2 py-0.5">
                            {base.startsWith('/agent') ? (c.intent === 'employer' ? 'ADD EMPLOYER' : 'ADD CANDIDATE') : 'VIEW'} →
                          </span>
                        </div>
                      </button>
                    );
                  })}
              </div>
              <div className="text-center text-[11px] text-gray-400 py-2 border-t border-gray-100">
                {base.startsWith('/agent') ? 'Click a card to add it as a candidate' : 'Click a number to view its first message'}
              </div>
            </>
          ) : (
            <>
              <div className="bg-gradient-to-r from-[#25D366] to-[#1FBF5B] text-white px-4 py-3 flex items-center gap-3">
                <button
                  onClick={() => { setSelected(null); setMessages([]); }}
                  className="text-white font-bold text-sm"
                >
                  ← Back
                </button>
                <div className="min-w-0">
                  <div className="font-bold text-sm truncate">{selected.contact_name || selected.phone}</div>
                  <a
                    href={`https://wa.me/91${selected.phone}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-white/90 underline"
                  >
                    {selected.phone} · open in WhatsApp
                  </a>
                </div>
              </div>
              <div className="max-h-[400px] overflow-y-auto p-3 space-y-2 bg-[#ECE5DD]">
                {loading && <div className="text-center text-sm text-gray-500">Loading…</div>}
                {!loading && messages.length === 0 && (
                  <div className="text-center text-sm text-gray-500">No messages</div>
                )}
                {!loading &&
                  messages.map((m) => (
                    <div key={m.id} className="bg-white rounded-lg rounded-tl-none px-3 py-2 shadow-sm max-w-[85%]">
                      <div className="text-sm break-words">
                        {m.body || (
                          <span className="text-xs text-gray-500">[{m.media_type || 'media message'}]</span>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-1 text-right">{stamp(m.created_at)}</div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
